import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapper, TableWrapper } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { importInvestorEntitiesQuickFilterOptions } from '../../../constants';
import { IGroupImportComparison, TypeWindow } from '../../../types';
import { columns } from '../constants';
import MobileTable from './mobileTable';
import TableBody from './tableBody';

interface TableProps {
  tableData: IGroupImportComparison[];
  openGroupImportDetailPage: (type: TypeWindow, row: IGroupImportComparison) => void;
  sort: ISort;
  setSort: Dispatch<SetStateAction<ISort>>;
  search: string;
  onChangeSearch: (value: string) => void;
  primaryFilter: string;
  changePrimaryFilterValue: (filter: string) => void;
}

const Table = ({
  tableData,
  openGroupImportDetailPage,
  sort,
  setSort,
  search,
  onChangeSearch,
  primaryFilter,
  changePrimaryFilterValue
}: TableProps) => {
  const { isMobile } = useResponsive();

  const [sortedTableData, setSortedTableData] = useState<IGroupImportComparison[]>([]);

  useEffect(() => {
    const sortedData = [...tableData].sort((a, b) => {
      switch (sort.key) {
        case 'HOUSEHOLD':
          return sort.asc ? a.householdName.localeCompare(b.householdName) : b.householdName.localeCompare(a.householdName);
        case 'GROUP_NAME':
          return sort.asc ? a.groupName.localeCompare(b.groupName) : b.groupName.localeCompare(a.groupName);
        case 'TOTAL_GROUPED':
          return sort.asc ? a.totalGrouped - b.totalGrouped : b.totalGrouped - a.totalGrouped;
        case 'NEWLY_GROUPED':
          return sort.asc ? a.newlyGrouped - b.newlyGrouped : b.newlyGrouped - a.newlyGrouped;
        case 'UNGROUPED':
          return sort.asc ? a.ungrouped - b.ungrouped : b.ungrouped - a.ungrouped;
        case 'PRIMARY_ENTITY':
          return sort.asc ? a.primaryEntityName.localeCompare(b.primaryEntityName) : b.primaryEntityName.localeCompare(a.primaryEntityName);
        case 'STATUS':
          return sort.asc ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
        default:
          return 0;
      }
    });

    setSortedTableData(sortedData);
  }, [sort, tableData]);

  const filteredTableData = () => {
    const newTableData = [...sortedTableData];
    const searchLower = search.toLowerCase();
    const filteredTableData = newTableData.filter(
      (item) => (item.householdName ?? '').toLowerCase().includes(searchLower) || (item.primaryEntityName ?? '').toLowerCase().includes(searchLower)
    );

    switch (primaryFilter) {
      case 'Updates Only':
        return filteredTableData.filter((item) => item.status !== 'error' && item.status !== 'unchanged');
      case 'Errors Only':
        return filteredTableData.filter((item) => item.status === 'error');
      default:
        return filteredTableData;
    }
  };

  return (
    <>
      <FilterPage
        search={{
          value: search,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        quickFilter={{
          title: 'Show',
          value: primaryFilter,
          onChange: changePrimaryFilterValue,
          defaultValue: 'All Rows',
          options: importInvestorEntitiesQuickFilterOptions
        }}
        resultsValue={filteredTableData().length}
        useNewFilters
      />
      <TableWrapper padding="0">
        {isMobile ? (
          <MobileTable tableData={filteredTableData()} openGroupImportDetailPage={openGroupImportDetailPage} />
        ) : (
          <>
            <TableHeader refetch={() => null as any} columns={columns} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              <TableBody tableData={filteredTableData()} openGroupImportDetailPage={openGroupImportDetailPage} />
            </TableBodyWrapper>
          </>
        )}
      </TableWrapper>
    </>
  );
};

export default Table;
